const FORMAL_TASK_STATUSES = new Set([
  'planned',
  'assigned',
  'accepted',
  'en_route',
  'arrived',
  'completed',
  'cancelled',
  'failed',
]);

const ASSIGNMENT_ERROR_CODES = new Set([
  'ASSIGNMENT_ACTIVE_CONFLICT',
  'ASSIGNMENT_DATA_ANOMALY',
  'ASSIGNMENT_DRIVER_NOT_ELIGIBLE',
  'ASSIGNMENT_SCHEDULE_REQUIRED',
  'ASSIGNMENT_SCHEDULE_IN_PAST',
  'ASSIGNMENT_PERMISSION_DENIED',
  'ASSIGNMENT_CONTAINER_NOT_FOUND',
  'ASSIGNMENT_NEXT_LEG_INVALID',
  'ASSIGNMENT_STATE_CHANGED',
  'ASSIGNMENT_WRITE_FAILED',
]);
const CANCELLATION_ERROR_CODES = new Set([
  'CANCELLATION_PERMISSION_DENIED',
  'CANCELLATION_TASK_NOT_FOUND',
  'CANCELLATION_REASON_INVALID',
  'CANCELLATION_NOT_ACTIVE',
  'CANCELLATION_DATA_ANOMALY',
  'CANCELLATION_STATE_CHANGED',
  'CANCELLATION_REQUEST_CONFLICT',
  'CANCELLATION_WRITE_FAILED',
]);
const DISPATCH_EDIT_ERROR_CODES = new Set([
  'EDIT_PERMISSION_DENIED', 'EDIT_TASK_NOT_FOUND', 'EDIT_REQUEST_INVALID',
  'EDIT_STATE_CHANGED', 'EDIT_TERMINAL_TASK', 'EDIT_ROUTE_STATE_INVALID',
  'EDIT_REQUEST_CONFLICT', 'EDIT_WRITE_FAILED',
]);
const CANCELLATION_REASONS = [
  ['driver_unavailable', 'Driver unavailable', '司机无法执行任务'],
  ['tractor_or_chassis_unavailable', 'Tractor or chassis unavailable', '牵引车或底盘无法使用'],
  ['container_not_available', 'Container not available', '货柜尚不可提取'],
  ['appointment_or_schedule_changed', 'Appointment or schedule changed', '预约或计划时间变更'],
  ['terminal_or_route_disruption', 'Terminal or route disruption', '码头或运输路线受阻'],
  ['customer_cancelled_leg', 'Customer cancelled this transport leg', '顾客取消本次运输段'],
  ['dispatch_correction', 'Dispatch record correction', '派车记录纠错'],
  ['operational_replan', 'Internal dispatch replan', '内部调度重排'],
  ['other', 'Other reason', '其他原因'],
];
const ACTIVE_TASK_STATUSES = new Set(['planned', 'assigned', 'accepted', 'en_route', 'arrived']);

function referenceId(reference) {
  return String(reference?.id || '').trim();
}

export function isDispatchAssignmentAllowed(container) {
  if (!container || typeof container !== 'object' || Array.isArray(container)) return false;
  const status = String(container.status || '').trim();
  return container.archived !== true
    && container.archived_by_restore !== true
    && container.is_empty_returned !== true
    && status !== 'empty_returned'
    && status !== 'cancelled';
}

function exactReference(reference, collectionName, expectedId = null) {
  const id = referenceId(reference);
  return id.length > 0
    && reference?.path === `${collectionName}/${id}`
    && (expectedId === null || id === expectedId);
}

function validTaskDate(value) {
  if (!value || typeof value?.toDate !== 'function') return false;
  try {
    const date = value.toDate();
    return date instanceof Date && Number.isFinite(date.getTime());
  } catch (_) {
    return false;
  }
}

export function eligibleDriverHints(drivers) {
  return (Array.isArray(drivers) ? drivers : [])
    .filter((driver) => {
      const uid = String(driver?.user_uid || '').trim();
      return driver?.active === true
        && driver?.archived_by_restore !== true
        && uid.length > 0
        && driver?.user_ref?.path === `users/${uid}`;
    })
    .sort((left, right) => String(left.full_name || '').localeCompare(String(right.full_name || '')));
}

export function formalDispatchSummary(container, task) {
  const currentTaskId = referenceId(container?.current_task_ref);
  const latestTaskId = referenceId(container?.latest_dispatch_task_ref);
  if ((container?.current_task_ref && !exactReference(container.current_task_ref, 'dispatch_tasks'))
    || (container?.latest_dispatch_task_ref && !exactReference(container.latest_dispatch_task_ref, 'dispatch_tasks'))) {
    return { state: 'anomaly', driverName: null, taskId: currentTaskId || latestTaskId || null };
  }
  if (currentTaskId && latestTaskId && currentTaskId !== latestTaskId) {
    return { state: 'anomaly', driverName: null, taskId: currentTaskId };
  }
  const authoritativeTaskId = currentTaskId || latestTaskId;
  if (!authoritativeTaskId) {
    return { state: 'not_dispatched', driverName: null, taskId: null };
  }
  const taskContainerId = referenceId(task?.container_ref);
  const valid = task?.id === authoritativeTaskId
    && task?.archived_by_restore === false
    && exactReference(task?.container_ref, 'containers', container?.id)
    && taskContainerId === container?.id
    && exactReference(task?.driver_ref, 'drivers')
    && typeof task?.driver_uid === 'string'
    && task.driver_uid.trim().length > 0
    && FORMAL_TASK_STATUSES.has(task?.status)
    && ['load_out_to_yard', 'load_out_to_customer', 'pickup_empty', 'return_empty'].includes(task?.leg_type)
    && Number.isInteger(task?.sequence)
    && task.sequence > 0
    && validTaskDate(task?.scheduled_start_at)
    && typeof task?.driver_name === 'string'
    && task.driver_name.trim().length > 0;
  if (!valid) {
    return { state: 'anomaly', driverName: null, taskId: authoritativeTaskId };
  }
  if (task.status === 'cancelled' || task.status === 'failed') {
    return { state: task.status, driverName: null, taskId: task.id };
  }
  if (task.status === 'completed') {
    return { state: 'completed', driverName: null, taskId: task.id };
  }
  return {
    state: task.status,
    driverName: task.driver_name.trim(),
    taskId: task.id,
  };
}

export function dispatchListLabelKey(summary) {
  if (summary?.state === 'cancelled') return 'dispatch.taskCancelledNotDispatched';
  if (summary?.state === 'failed') return 'dispatch.taskFailedNotDispatched';
  if (summary?.state === 'completed') return 'dispatch.taskCompletedNoActiveDriver';
  if (summary?.state === 'anomaly') return 'dispatch.dataReview';
  return 'dispatch.notOfficiallyDispatched';
}

export function assignmentOpeningState(container, tasks) {
  if (!isDispatchAssignmentAllowed(container)) return { state: 'terminal', task: null };
  const visible = (Array.isArray(tasks) ? tasks : []).filter((task) => task?.archived_by_restore !== true);
  if (visible.length > 100 || visible.some((task) => !FORMAL_TASK_STATUSES.has(task?.status))) {
    return { state: 'anomaly', task: null };
  }
  const currentTaskId = referenceId(container?.current_task_ref);
  const latestTaskId = referenceId(container?.latest_dispatch_task_ref);
  if (currentTaskId && latestTaskId && currentTaskId !== latestTaskId) {
    return { state: 'anomaly', task: null };
  }
  const authoritativeTaskId = currentTaskId || latestTaskId;
  if (authoritativeTaskId && !visible.some((task) => task?.id === authoritativeTaskId)) {
    return { state: 'anomaly', task: null };
  }
  const active = visible.filter((task) => ACTIVE_TASK_STATUSES.has(task?.status));
  if (active.length > 1) return { state: 'anomaly', task: null };
  if (active.length === 1) {
    const task = active[0];
    const canonical = exactReference(container?.current_task_ref, 'dispatch_tasks', task.id)
      && exactReference(container?.latest_dispatch_task_ref, 'dispatch_tasks', task.id)
      && exactReference(task?.container_ref, 'containers', container?.id)
      && exactReference(task?.driver_ref, 'drivers')
      && exactReference(container?.assigned_driver_ref, 'drivers', referenceId(task.driver_ref))
      && typeof task?.driver_uid === 'string' && task.driver_uid.trim().length > 0
      && container?.assigned_driver_uid === task.driver_uid
      && typeof task?.driver_name === 'string' && task.driver_name.trim().length > 0
      && container?.assigned_driver_name === task.driver_name
      && Number.isInteger(task?.sequence) && task.sequence > 0
      && ['load_out_to_yard', 'load_out_to_customer', 'pickup_empty', 'return_empty'].includes(task?.leg_type)
      && validTaskDate(task?.updated_at);
    if (!canonical) return { state: 'anomaly', task: null };
    return { state: 'active_conflict', task };
  }
  return { state: 'ready', task: null };
}

export function buildDispatchAssignmentRequest(values) {
  if (!values?.scheduledStartAt) throw new Error('Schedule required');
  const scheduledStart = new Date(values.scheduledStartAt);
  if (Number.isNaN(scheduledStart.getTime())) throw new Error('Schedule required');
  return {
    containerId: String(values.containerId || '').trim(),
    driverId: String(values.driverId || '').trim(),
    legType: String(values.legType || '').trim(),
    scheduledStartAt: scheduledStart.toISOString(),
    origin: {
      name: String(values.originName || '').trim(),
      address: String(values.originAddress || '').trim(),
      type: String(values.originType || '').trim(),
    },
    destination: {
      name: String(values.destinationName || '').trim(),
      address: String(values.destinationAddress || '').trim(),
      type: String(values.destinationType || '').trim(),
    },
    dispatcherNotes: String(values.dispatcherNotes || '').trim(),
  };
}

export function buildDispatchEditRequest(values) {
  const expected = values?.expectedUpdatedAt instanceof Date
    ? values.expectedUpdatedAt : new Date(values?.expectedUpdatedAt);
  const start = new Date(values?.scheduledStartAt);
  const end = values?.scheduledEndAt ? new Date(values.scheduledEndAt) : null;
  if (!Number.isFinite(expected.getTime())) throw new Error('Edit state required');
  if (!Number.isFinite(start.getTime())) throw new Error('Edit schedule required');
  if (end && (!Number.isFinite(end.getTime()) || end < start)) throw new Error('Edit schedule invalid');
  const point = (prefix) => ({
    name: String(values?.[`${prefix}Name`] || '').trim(),
    address: String(values?.[`${prefix}Address`] || '').trim(),
    type: String(values?.[`${prefix}Type`] || '').trim(),
  });
  return {
    taskId: String(values?.taskId || '').trim(),
    requestId: String(values?.requestId || '').trim(),
    expectedUpdatedAt: expected.toISOString(),
    scheduledStartAt: start.toISOString(),
    scheduledEndAt: end ? end.toISOString() : null,
    origin: point('origin'),
    destination: point('destination'),
    dispatcherNotes: String(values?.dispatcherNotes || '').trim(),
  };
}

export function cancellationReasonOptions(locale = 'en') {
  const chinese = String(locale).toLowerCase().startsWith('zh');
  return CANCELLATION_REASONS.map(([code, en, zh]) => ({ code, label: chinese ? zh : en }));
}

export function createCancellationRequestId(cryptoProvider = globalThis.crypto) {
  if (typeof cryptoProvider?.randomUUID === 'function') return cryptoProvider.randomUUID();
  if (typeof cryptoProvider?.getRandomValues !== 'function') throw new Error('Secure request id unavailable');
  const bytes = new Uint8Array(16);
  cryptoProvider.getRandomValues(bytes);
  return `cancel-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}

export function setCancellationFormPending(form, pending, { retryOnly = false, submit = null } = {}) {
  const controls = Array.from(form?.querySelectorAll?.('input, select, textarea, button') || []);
  controls.forEach((control) => { control.disabled = pending || retryOnly; });
  if (retryOnly && submit) submit.disabled = false;
}

export function createCancellationRetryController(operation) {
  let pending = false;
  let ambiguousPayload = null;
  return {
    get hasAmbiguousRetry() {
      return ambiguousPayload !== null;
    },
    async submit(payload) {
      if (pending) throw new Error('Cancellation already pending');
      const request = ambiguousPayload || Object.freeze({ ...payload });
      pending = true;
      try {
        const result = await operation(request);
        ambiguousPayload = null;
        return result;
      } catch (error) {
        ambiguousPayload = cancellationErrorCode(error) === 'CANCELLATION_WRITE_FAILED'
          ? request
          : null;
        throw error;
      } finally {
        pending = false;
      }
    },
  };
}

export function buildDispatchCancellationRequest(values) {
  const reasonCode = String(values?.cancelReasonCode || '').trim();
  if (!CANCELLATION_REASONS.some(([code]) => code === reasonCode)) {
    throw new Error('Cancellation reason required');
  }
  const explanation = String(values?.cancelReasonExplanation || '').trim();
  if (reasonCode === 'other' && (explanation.length < 1 || explanation.length > 500)) {
    throw new Error('Cancellation explanation required');
  }
  if (reasonCode !== 'other' && explanation.length > 0) {
    throw new Error('Cancellation explanation is not allowed');
  }
  const expected = values?.expectedUpdatedAt instanceof Date
    ? values.expectedUpdatedAt
    : new Date(values?.expectedUpdatedAt);
  if (!Number.isFinite(expected.getTime())) throw new Error('Cancellation state required');
  return {
    taskId: String(values?.taskId || '').trim(),
    cancelReasonCode: reasonCode,
    cancelReasonExplanation: reasonCode === 'other' ? explanation : null,
    expectedStatus: String(values?.expectedStatus || '').trim(),
    expectedUpdatedAt: expected.toISOString(),
    requestId: String(values?.requestId || '').trim(),
  };
}

export function createSingleSubmissionGuard(operation) {
  let pending = false;
  return {
    get pending() {
      return pending;
    },
    async submit(value) {
      if (pending) throw new Error('Assignment already pending');
      pending = true;
      try {
        return await operation(value);
      } finally {
        pending = false;
      }
    },
  };
}

export function assignmentErrorCode(error) {
  const code = error?.details?.code;
  return ASSIGNMENT_ERROR_CODES.has(code) ? code : 'ASSIGNMENT_WRITE_FAILED';
}

export function cancellationErrorCode(error) {
  const code = error?.details?.code;
  return CANCELLATION_ERROR_CODES.has(code) ? code : 'CANCELLATION_WRITE_FAILED';
}

export function dispatchEditErrorCode(error) {
  const code = error?.details?.code;
  return DISPATCH_EDIT_ERROR_CODES.has(code) ? code : 'EDIT_WRITE_FAILED';
}
